// Frontend/src/hooks/useBuildings.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listBuildings,
  getBuilding,
  createBuilding,
  updateBuilding,
  deleteBuilding,
  getBuildingStats,
  importBuildingsCSV,
  importBuildingUnitsCSV,
  importBuildingResidentsCSV,
  downloadBuildingsTemplate,
  type BuildingList,
  type Building,
  type CreateBuildingData,
  type UpdateBuildingData,
  type BuildingStats,
  type CSVImportResult,
} from "@/api/buildings";

/**
 * Query key factory for buildings
 */
export const buildingKeys = {
  all: ["buildings"] as const,
  lists: () => [...buildingKeys.all, "list"] as const,
  list: () => [...buildingKeys.lists()] as const,
  details: () => [...buildingKeys.all, "detail"] as const,
  detail: (id: string) => [...buildingKeys.details(), id] as const,
  stats: (id: string) => [...buildingKeys.all, "stats", id] as const,
};

/**
 * Fetch all buildings
 */
export function useBuildings() {
  return useQuery<BuildingList[], Error>({
    queryKey: buildingKeys.list(),
    queryFn: listBuildings,
  });
}

/**
 * Fetch a single building by ID
 */
export function useBuilding(id: string) {
  return useQuery<Building, Error>({
    queryKey: buildingKeys.detail(id),
    queryFn: () => getBuilding(id),
    enabled: !!id,
  });
}

/**
 * Fetch statistics for a building
 */
export function useBuildingStats(id: string) {
  return useQuery<BuildingStats, Error>({
    queryKey: buildingKeys.stats(id),
    queryFn: () => getBuildingStats(id),
    enabled: !!id,
  });
}

/**
 * Create a new building
 */
export function useCreateBuilding() {
  const queryClient = useQueryClient();

  return useMutation<Building, Error, { data: CreateBuildingData; rifImageFile?: File | null }>({
    mutationFn: ({ data, rifImageFile }) => createBuilding(data, rifImageFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
    },
  });
}

/**
 * Update an existing building
 */
export function useUpdateBuilding() {
  const queryClient = useQueryClient();

  return useMutation<Building, Error, { id: string; data: UpdateBuildingData; rifImageFile?: File | null }>({
    mutationFn: ({ id, data, rifImageFile }) => updateBuilding(id, data, rifImageFile),
    onSuccess: (data) => {
      queryClient.setQueryData(buildingKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: buildingKeys.stats(data.id) });
    },
  });
}

/**
 * Delete a building
 */
export function useDeleteBuilding() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteBuilding,
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: buildingKeys.detail(deletedId) });
      queryClient.removeQueries({ queryKey: buildingKeys.stats(deletedId) });
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
    },
  });
}

/**
 * Import buildings from CSV
 */
export function useImportBuildingsCSV() {
  const queryClient = useQueryClient();

  return useMutation<CSVImportResult, Error, File>({
    mutationFn: importBuildingsCSV,
    onSuccess: () => {
      // Refetch buildings list after import
      queryClient.invalidateQueries({ queryKey: buildingKeys.lists() });
    },
  });
}

/**
 * Import units for a specific building from CSV
 */
export function useImportBuildingUnitsCSV() {
  const queryClient = useQueryClient();

  return useMutation<CSVImportResult, Error, { buildingId: string; file: File }>({
    mutationFn: ({ buildingId, file }) => importBuildingUnitsCSV(buildingId, file),
    onSuccess: (_, { buildingId }) => {
      // Invalidate units list
      queryClient.invalidateQueries({ queryKey: ["units"] });
      // Invalidate building stats
      queryClient.invalidateQueries({ queryKey: buildingKeys.stats(buildingId) });
    },
  });
}

/**
 * Import residents for a specific building from CSV
 */
export function useImportBuildingResidentsCSV() {
  const queryClient = useQueryClient();

  return useMutation<CSVImportResult, Error, { buildingId: string; file: File }>({
    mutationFn: ({ buildingId, file }) => importBuildingResidentsCSV(buildingId, file),
    onSuccess: (_, { buildingId }) => {
      // Invalidate residents list
      queryClient.invalidateQueries({ queryKey: ["residents"] });
      // Invalidate building stats
      queryClient.invalidateQueries({ queryKey: buildingKeys.stats(buildingId) });
    },
  });
}

/**
 * Download buildings CSV template
 */
export function useDownloadBuildingsTemplate() {
  return useMutation<Blob, Error, void>({
    mutationFn: downloadBuildingsTemplate,
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "buildings_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
